import { Company } from "@models/companies/company";
import { Entity } from "@models/companies/entity";
import { EntityData } from "@models/companies/entity-data";
import { IndividualData } from "@models/individuals/individual-data";
import { nonBeneficialOwnerLabel } from "../../forms/asic-forms/shares/constants/non-beneficial-owner-label";
import { Guid } from "@shared/utils/guid.helper";
import { DatepickerHelper } from '@shared/validators/date-picker-form-validators';
import { Contact } from "../common/contact";
import { RelationshipType } from "./relationship-type.enum";
import { Individual } from "../individuals/individual";
import { Trust } from "../trusts/trust";


export class Relationship {
  relationshipId: string;
  organisationId?: string;
  contactId?: string;
  contact?: Contact;
  start?: Date;
  end?: Date;
  type!: RelationshipType;
  details?: ShareholderRelationshipDetails | OfficerRelationshipDetails | TrusteeRelationshipDetails;
  individualDataOverride: IndividualData | null;
  entityDataOverride: EntityData | null;
  entityId: string;
  entity?: Company | Trust | null;
  entityOverride?: Entity | Company | Trust;
  individualId: string | null;
  individual?: Individual;
  relatedEntityId: string | null;
  relatedEntity?: Entity | Company | Trust;


  //temporary
  isPosted = true;
  // only for front-end part

  key= '';

  constructor(relationship: Partial<Relationship> = {}) {
    this.relationshipId = relationship.relationshipId ?? Guid.EmptyGuid;
    this.organisationId = relationship.organisationId;
    this.contactId = relationship.contactId;
    this.contact = relationship.contact;
    this.start = relationship.start ?? undefined;
    this.end = relationship.end ?? undefined;
    this.type = relationship.type!;
    this.details = relationship.details;
    this.individualDataOverride = relationship.individualDataOverride ? new IndividualData(relationship.individualDataOverride) : null;
    this.entityId = relationship.entityId ?? Guid.EmptyGuid;
    this.entity = relationship.entity ? this.setEntity(relationship.entity) : null;
    this.entityDataOverride = relationship.entityDataOverride ?? null;
    this.entityOverride = relationship.entityOverride ?? undefined;
    this.individualId = relationship.individualId ?? null;
    this.individual = relationship.individual ?? undefined;
    this.relatedEntityId = relationship.relatedEntityId ?? null;
    this.isPosted = relationship.isPosted ?? true;
    this.key = this.individualId ?? this.relatedEntityId ?? '';
  }

  get isResigned(): boolean {
   return this.end != undefined && new Date(this.end) < new Date();
  }

  setEntity (entity?: Entity) : Company | Trust | null {
    if(!entity)
      return null;

    switch(entity.$type) {
      case Company.$type:
        return new Company(entity);
      case Trust.$type:
        return new Trust(entity);
      default:
        throw new Error('Invalid entity type');
    }
  }

  static prepareToRequest(relationship: Relationship): Relationship {
    relationship.start = relationship.start ? DatepickerHelper.buildDateString(new Date(relationship.start)) : relationship.start;
    relationship.end = relationship.end ? DatepickerHelper.buildDateString(new Date(relationship.end)) : relationship.end;
    relationship.individualDataOverride = relationship.individualDataOverride ? IndividualData.prepareToRequest(relationship.individualDataOverride) : null;
    relationship.entityDataOverride = relationship.entityDataOverride ? EntityData.prepareToRequest(relationship.entityDataOverride) : null;
    if (relationship.details?.$type === new OfficerRelationshipDetails().$type && (relationship.details as OfficerRelationshipDetails).expiryDate)
      (relationship.details as OfficerRelationshipDetails).expiryDate = DatepickerHelper.buildDateString((relationship.details as OfficerRelationshipDetails).expiryDate!);
    return relationship;
  }
}

export class ShareholderRelationshipDetails {
  $type = "ShareholderRelationshipDetails";
  isBeneficialOwner: boolean;
  beneficialOwner: string;
  beneficialOwnerEntityId: string | null;
  authorisedSignatories?: IndividualData[];

  get beneficialOwnerLabel(): string {
    return this.isBeneficialOwner ? "" : (this.beneficialOwner ? this.beneficialOwner : nonBeneficialOwnerLabel);
}

  constructor(details: Partial<ShareholderRelationshipDetails> = {}) {
      this.isBeneficialOwner = details?.isBeneficialOwner ?? false;
      this.beneficialOwner = details?.beneficialOwner ?? '';
      this.beneficialOwnerEntityId = details?.beneficialOwnerEntityId ?? null;
      this.authorisedSignatories = details?.authorisedSignatories?.map(signatory => new IndividualData(signatory));
  }
}

export class OfficerRelationshipDetails {
  $type = "OfficerRelationshipDetails";
  isAlternativeDirector: boolean;
  alternativeDirectorFor?: string;
  termsOfAppointment?: string;
  expiryDate?: Date;
  officerAsicId?: string;

  constructor(details: Partial<OfficerRelationshipDetails> = {}) {
    this.isAlternativeDirector = details.isAlternativeDirector ?? false;
    this.alternativeDirectorFor = details.alternativeDirectorFor ?? undefined;
    this.termsOfAppointment = details.termsOfAppointment ?? undefined;
    this.expiryDate = details.expiryDate ? new Date(details.expiryDate) : undefined;
    this.officerAsicId = details.officerAsicId ?? undefined;
  }
}

export class TrusteeRelationshipDetails {
  static $type = "TrusteeRelationshipDetails";
  $type = "TrusteeRelationshipDetails";
  authorisedSignatories: IndividualData[];
  directors: IndividualData[];

  constructor(details: Partial<TrusteeRelationshipDetails> = {}) {
    this.authorisedSignatories = details.authorisedSignatories ?? [];
    this.directors = details.directors ?? [];
  }
}
